import React from 'react';
import {createPortal} from 'react-dom';
import * as FiIcons from 'react-icons/fi';
import {clsx,type ClassValue} from 'clsx';
import {twMerge} from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const SafeIcon=({icon: Icon,name,className,...props}: any)=> {
  if (Icon) return <Icon className={className} {...props} />;
  const Fallback=name ? (FiIcons as any)[`Fi${name}`] : null;
  return Fallback ? <Fallback className={className} {...props} /> : <FiIcons.FiAlertTriangle className={className} {...props} />;
};

export const Card=({children,className=''}: {children: React.ReactNode;className?: string})=> (
  <div className={cn('min-w-0 max-w-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden',className)}>{children}</div>
);

export const Button=({children,variant='primary',size='md',className='',...props}: any)=> {
  const variants={
    primary: 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm',
    secondary: 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 shadow-sm',
    danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm',
    ghost: 'hover:bg-gray-100 text-gray-700',
  };
  const sizes={
    sm: 'min-h-10 px-3 py-1.5 text-xs rounded-md',
    md: 'min-h-11 px-4 py-2 text-sm rounded-lg',
    lg: 'min-h-12 px-5 py-2.5 text-base rounded-xl',
  };
  return (
    <button className={cn(
      'font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30',
      variants[variant as keyof typeof variants],
      sizes[size as keyof typeof sizes],
      className
    )} {...props}>
      {children}
    </button>
  );
};

const normalizeDateInputValue=(value: any): string=> {
  if (value===undefined || value===null || value==='') return '';
  const raw=String(value).trim();
  const isoDatePrefix=raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return isoDatePrefix ? isoDatePrefix[1] : raw;
};

const parseIsoDate=(value: string): Date | null=> {
  const match=normalizeDateInputValue(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year=Number(match[1]);
  const month=Number(match[2]);
  const day=Number(match[3]);
  const date=new Date(year,month - 1,day);
  if (date.getFullYear()!==year || date.getMonth()!==month - 1 || date.getDate()!==day) return null;
  return date;
};

const toIsoDate=(date: Date): string=> `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;

const formatDateForInput=(value: string): string=> {
  const date=parseIsoDate(value);
  return date ? `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth() + 1).padStart(2,'0')}/${date.getFullYear()}` : '';
};

const parseTypedDate=(value: string): string | null=> {
  const raw=String(value || '').trim();
  if (!raw) return '';
  const iso=raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const australian=raw.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  const year=iso ? Number(iso[1]) : australian ? Number(australian[3]) : NaN;
  const month=iso ? Number(iso[2]) : australian ? Number(australian[2]) : NaN;
  const day=iso ? Number(iso[3]) : australian ? Number(australian[1]) : NaN;
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const date=new Date(year,month - 1,day);
  if (date.getFullYear()!==year || date.getMonth()!==month - 1 || date.getDate()!==day) return null;
  return toIsoDate(date);
};

const addDateDays=(date: Date,days: number): Date=> {
  const next=new Date(date.getFullYear(),date.getMonth(),date.getDate());
  next.setDate(next.getDate() + days);
  return next;
};

const isSameCalendarDay=(a: Date | null,b: Date): boolean=> Boolean(a && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate());

const SmartDateInput=({label,error,className,value,defaultValue,onChange,disabled,min,max,placeholder='dd/mm/yyyy',name,id,required,onBlur,onFocus,onKeyDown,datePresets=true,...props}: any)=> {
  const controlled=value!==undefined;
  const [internalValue,setInternalValue]=React.useState(()=> normalizeDateInputValue(defaultValue));
  const resolvedValue=normalizeDateInputValue(controlled ? value : internalValue);
  const [displayValue,setDisplayValue]=React.useState(()=> formatDateForInput(resolvedValue));
  const [isOpen,setIsOpen]=React.useState(false);
  const [viewMonth,setViewMonth]=React.useState<Date>(()=> {
    const selected=parseIsoDate(resolvedValue);
    const base=selected || new Date();
    return new Date(base.getFullYear(),base.getMonth(),1);
  });
  const anchorRef=React.useRef<HTMLDivElement | null>(null);
  const popoverRef=React.useRef<HTMLDivElement | null>(null);
  const [popoverStyle,setPopoverStyle]=React.useState<React.CSSProperties>({});

  React.useEffect(()=> {
    setDisplayValue(formatDateForInput(resolvedValue));
  },[resolvedValue]);

  const updatePopoverPosition=React.useCallback(()=> {
    if (!anchorRef.current || typeof window==='undefined') return;
    const rect=anchorRef.current.getBoundingClientRect();
    const width=Math.min(332,Math.max(280,window.innerWidth - 24));
    const measuredHeight=popoverRef.current?.offsetHeight || 430;
    const left=Math.min(Math.max(12,rect.left),Math.max(12,window.innerWidth - width - 12));
    const belowTop=rect.bottom + 8;
    const top=belowTop + measuredHeight <=window.innerHeight - 12 ? belowTop : Math.max(12,rect.top - measuredHeight - 8);
    setPopoverStyle({position: 'fixed',left,top,width,zIndex: 200});
  },[]);

  React.useEffect(()=> {
    if (!isOpen) return;
    const selected=parseIsoDate(resolvedValue);
    const base=selected || new Date();
    setViewMonth(new Date(base.getFullYear(),base.getMonth(),1));
    const frame=window.requestAnimationFrame(updatePopoverPosition);
    const handlePointer=(event: MouseEvent | TouchEvent)=> {
      const target=event.target as Node;
      if (anchorRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const handleEscape=(event: KeyboardEvent)=> {if (event.key==='Escape') setIsOpen(false);};
    window.addEventListener('resize',updatePopoverPosition);
    window.addEventListener('scroll',updatePopoverPosition,true);
    document.addEventListener('mousedown',handlePointer);
    document.addEventListener('touchstart',handlePointer);
    document.addEventListener('keydown',handleEscape);
    return ()=> {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize',updatePopoverPosition);
      window.removeEventListener('scroll',updatePopoverPosition,true);
      document.removeEventListener('mousedown',handlePointer);
      document.removeEventListener('touchstart',handlePointer);
      document.removeEventListener('keydown',handleEscape);
    };
  },[isOpen,resolvedValue,updatePopoverPosition]);

  const inRange=(iso: string): boolean=> (!min || iso>=normalizeDateInputValue(min)) && (!max || iso<=normalizeDateInputValue(max));

  const emitValue=(nextValue: string)=> {
    const normalized=normalizeDateInputValue(nextValue);
    if (normalized && !inRange(normalized)) return false;
    if (!controlled) setInternalValue(normalized);
    setDisplayValue(formatDateForInput(normalized));
    if (onChange) onChange({target: {value: normalized,name},currentTarget: {value: normalized,name}});
    return true;
  };

  const selectDate=(date: Date)=> {
    const iso=toIsoDate(date);
    if (!emitValue(iso)) return;
    setViewMonth(new Date(date.getFullYear(),date.getMonth(),1));
    setIsOpen(false);
  };

  const chooseRelative=(days: number)=> selectDate(addDateDays(new Date(),days));

  const commitTypedValue=()=> {
    if (!displayValue.trim()) {
      emitValue('');
      return;
    }
    const parsed=parseTypedDate(displayValue);
    if (parsed!==null && emitValue(parsed)) return;
    setDisplayValue(formatDateForInput(resolvedValue));
  };

  const selectedDate=parseIsoDate(resolvedValue);
  const today=new Date();
  const year=viewMonth.getFullYear();
  const month=viewMonth.getMonth();
  const monthStart=new Date(year,month,1);
  const mondayOffset=(monthStart.getDay() + 6) % 7;
  const calendarStart=new Date(year,month,1 - mondayOffset);
  const calendarDays=Array.from({length: 42},(_,index)=> addDateDays(calendarStart,index));
  const monthTitle=viewMonth.toLocaleDateString('en-AU',{month: 'long',year: 'numeric'});

  const popover=isOpen && typeof document!=='undefined' ? createPortal(
    <div ref={popoverRef} style={popoverStyle} className="rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden" role="dialog" aria-label="Choose date">
      {datePresets && (
        <div className="grid grid-cols-3 gap-2 p-3 border-b border-gray-100 bg-gray-50/80">
          <button type="button" onClick={()=> chooseRelative(0)} className="rounded-lg border border-brand-100 bg-white px-2 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30">Today</button>
          <button type="button" onClick={()=> chooseRelative(7)} className="rounded-lg border border-gray-200 bg-white px-2 py-2 text-xs font-semibold text-gray-700 hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30">Next 7 Days</button>
          <button type="button" onClick={()=> chooseRelative(30)} className="rounded-lg border border-gray-200 bg-white px-2 py-2 text-xs font-semibold text-gray-700 hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30">Next 30 Days</button>
        </div>
      )}
      <div className="flex items-center justify-between px-3 py-3">
        <button type="button" aria-label="Previous month" onClick={()=> setViewMonth(new Date(year,month - 1,1))} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30">
          <SafeIcon icon={FiIcons.FiChevronLeft} className="w-4 h-4" />
        </button>
        <button type="button" onClick={()=> setViewMonth(new Date(today.getFullYear(),today.getMonth(),1))} className="text-sm font-bold text-gray-900 rounded-lg px-2 py-1 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30" title="Jump to today's month">{monthTitle}</button>
        <button type="button" aria-label="Next month" onClick={()=> setViewMonth(new Date(year,month + 1,1))} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30">
          <SafeIcon icon={FiIcons.FiChevronRight} className="w-4 h-4" />
        </button>
      </div>
      <div className="px-3 pb-3">
        <div className="grid grid-cols-7 mb-1">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(day=> <div key={day} className="py-1 text-center text-[10px] font-bold uppercase tracking-wide text-gray-400">{day}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {calendarDays.map(date=> {
            const iso=toIsoDate(date);
            const selected=isSameCalendarDay(selectedDate,date);
            const isToday=isSameCalendarDay(today,date);
            const outside=date.getMonth()!==month;
            const disabledDate=!inRange(iso);
            return (
              <button key={iso} type="button" disabled={disabledDate} onClick={()=> selectDate(date)} className={cn(
                'relative h-9 rounded-lg text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:opacity-30 disabled:cursor-not-allowed',
                selected ? 'bg-brand-600 text-white shadow-sm hover:bg-brand-700' : outside ? 'text-gray-300 hover:bg-gray-50' : 'text-gray-700 hover:bg-brand-50 hover:text-brand-700',
                isToday && !selected && 'ring-1 ring-inset ring-brand-300 text-brand-700 font-bold'
              )} aria-label={date.toLocaleDateString('en-AU',{weekday: 'long',day: 'numeric',month: 'long',year: 'numeric'})} aria-current={isToday ? 'date' : undefined} aria-selected={selected}>
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2.5 bg-gray-50/70">
        <button type="button" onClick={()=> chooseRelative(0)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800">
          <SafeIcon icon={FiIcons.FiCalendar} className="w-3.5 h-3.5" /> Today
        </button>
        <button type="button" onClick={()=> {emitValue('');setIsOpen(false);}} className="text-xs font-medium text-gray-500 hover:text-gray-800">Clear</button>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className={cn('flex flex-col gap-1.5',className)}>
      {label && <label htmlFor={id} className="text-[13px] font-bold text-gray-700 ml-0.5">{label}</label>}
      <div ref={anchorRef} className="relative">
        {name && <input type="hidden" name={name} value={resolvedValue} disabled={disabled} />}
        <input
          {...props}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={disabled}
          required={required}
          value={displayValue}
          placeholder={placeholder}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-invalid={Boolean(error) || undefined}
          onClick={()=> {if (!disabled) setIsOpen(true);}}
          onFocus={(event)=> {if (!disabled) setIsOpen(true);if (onFocus) onFocus(event);}}
          onChange={(event)=> setDisplayValue(event.target.value)}
          onBlur={(event)=> {window.setTimeout(commitTypedValue,0);if (onBlur) onBlur(event);}}
          onKeyDown={(event)=> {
            if (event.key==='Enter') {event.preventDefault();commitTypedValue();setIsOpen(false);}
            if (event.key==='ArrowDown' && !isOpen) {event.preventDefault();setIsOpen(true);}
            if (onKeyDown) onKeyDown(event);
          }}
          className={cn(
            'w-full min-h-11 border border-gray-300 rounded-lg pl-3 pr-10 py-2 text-base xl:text-sm leading-5 outline-none transition-colors bg-white focus:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20 focus-visible:ring-offset-0 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
            error && 'border-red-500 focus:ring-red-500/10 focus:border-red-500'
          )}
        />
        <button type="button" tabIndex={-1} disabled={disabled} onMouseDown={(event)=> event.preventDefault()} onClick={()=> setIsOpen(open=> !open)} aria-label="Open calendar" className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-500 hover:text-brand-700 disabled:text-gray-300">
          <SafeIcon icon={FiIcons.FiCalendar} className="w-4 h-4" />
        </button>
      </div>
      {error && <span className="text-xs text-red-500 font-semibold ml-1">{error}</span>}
      {popover}
    </div>
  );
};

export const Input=({label,error,className,...props}: any)=> {
  if (props.type==='date') return <SmartDateInput label={label} error={error} className={className} {...props} />;
  return (
    <div className={cn('flex flex-col gap-1.5',className)}>
      {label && <label className="text-[13px] font-bold text-gray-700 ml-0.5">{label}</label>}
      <input className={cn(
        'w-full min-h-11 border border-gray-300 rounded-lg px-3 py-2 text-base xl:text-sm leading-5 outline-none transition-colors focus:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20 focus-visible:ring-offset-0',
        error && 'border-red-500 focus:ring-red-500/10 focus:border-red-500'
      )} {...props} />
      {error && <span className="text-xs text-red-500 font-semibold ml-1">{error}</span>}
    </div>
  );
};

export const TextArea=({label,error,className,...props}: any)=> (
  <div className={cn('flex flex-col gap-1.5',className)}>
    {label && <label className="text-[13px] font-bold text-gray-700 ml-0.5">{label}</label>}
    <textarea className={cn(
      'w-full border border-gray-300 rounded-lg px-3 py-2 text-base xl:text-sm leading-5 outline-none transition-colors focus:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20 focus-visible:ring-offset-0 min-h-[80px] max-h-[520px] resize-y overflow-auto',
      error && 'border-red-500 focus:ring-red-500/10 focus:border-red-500'
    )} {...props} />
    {error && <span className="text-xs text-red-500 font-semibold ml-1">{error}</span>}
  </div>
);

export const Select=({label,options,error,className,...props}: any)=> (
  <div className={cn('flex flex-col gap-1.5',className)}>
    {label && <label className="text-[13px] font-bold text-gray-700 ml-0.5">{label}</label>}
    <select className={cn(
      'w-full min-h-11 border border-gray-300 rounded-lg px-3 py-2 text-base xl:text-sm leading-5 outline-none transition-colors focus:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20 focus-visible:ring-offset-0 bg-white appearance-none cursor-pointer',
      error && 'border-red-500 focus:ring-red-500/10 focus:border-red-500'
    )} {...props}>
      {options.map((opt: any)=> (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {error && <span className="text-xs text-red-500 font-semibold ml-1">{error}</span>}
  </div>
);

export const Toggle=({label,description,checked,onChange,className}: any)=> (
  <div className={cn('flex items-center justify-between gap-4 py-2 px-1',className)}>
    <div className="flex min-w-0 flex-col">
      <span className="text-[13px] font-bold text-gray-900">{label}</span>
      {description && <span className="text-xs text-gray-500">{description}</span>}
    </div>
    <button
      type="button"
      onClick={()=> onChange(!checked)}
      className="relative inline-flex h-11 w-14 shrink-0 items-center justify-center rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
      aria-pressed={checked}
    >
      <span className={cn(
        'relative inline-flex h-6 w-11 rounded-full transition-colors duration-200 ease-in-out',
        checked ? 'bg-brand-600' : 'bg-gray-200'
      )}>
        <span className={cn(
          'pointer-events-none inline-block h-5 w-5 translate-y-0.5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          checked ? 'translate-x-5' : 'translate-x-0.5'
        )}/>
      </span>
    </button>
  </div>
);

export const Badge=({children,variant='info',className}: any)=> {
  const variants={
    success: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    warning: 'bg-amber-50 text-amber-700 border-amber-100',
    danger: 'bg-rose-50 text-rose-700 border-rose-100',
    info: 'bg-sky-50 text-sky-700 border-sky-100',
    neutral: 'bg-gray-50 text-gray-600 border-gray-100',
    brand: 'bg-brand-50 text-brand-700 border-brand-100',
  };
  return (
    <span className={cn(
      'px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap border',
      variants[variant as keyof typeof variants],
      className
    )}>
      {children}
    </span>
  );
};

// Shared, presentation-only table sorting. React row elements are reordered for
// display; the underlying repositories, arrays, selection and mutations are not changed.
export type TableSort={column: number;label: string;direction: 'asc' | 'desc'};
type TableSortValue=string | number | boolean | Date | null | undefined;
type TableSortingContext={
  enabled: boolean;
  sort: TableSort | null;
  changeSort: (column: number,label: string)=> void;
  manualSort: boolean;
};
const TableSorting=React.createContext<TableSortingContext | null>(null);
const TABLE_COLLATOR=new Intl.Collator('en-AU',{numeric:true,sensitivity:'base'});

const tableReactText=(value: any): string=> {
  if(value===null || value===undefined || typeof value==='boolean') return '';
  if(typeof value==='string' || typeof value==='number') return String(value);
  if(Array.isArray(value)) return value.map(tableReactText).filter(Boolean).join(' ');
  if(React.isValidElement(value)) return tableReactText((value.props as any).children);
  return '';
};

const tableChildrenOfType=(nodes: any,type: any): React.ReactElement<any>[]=> {
  const found: React.ReactElement<any>[]=[];
  React.Children.forEach(nodes,node=> {
    if(!React.isValidElement(node)) return;
    if(node.type===type) found.push(node);
    else if(node.type===React.Fragment) found.push(...tableChildrenOfType((node.props as any).children,type));
  });
  return found;
};

const tableHeadings=(children: any): string[]=> {
  const heads=tableChildrenOfType(children,Thead);
  if(heads.length!==1) return [];
  const rows=tableChildrenOfType(heads[0].props.children,Tr);
  if(rows.length!==1) return [];
  return tableChildrenOfType(rows[0].props.children,Th).map(col=>tableReactText(col.props.children).trim());
};

const tableColumnValue=(row: React.ReactElement<any>,column: number): TableSortValue=> {
  const cells=tableChildrenOfType(row.props.children,Td);
  const cell=cells[column];
  if(!cell || cell.props.colSpan) return null;
  if(cell.props.sortValue!==undefined) return cell.props.sortValue;
  return tableReactText(cell.props.children).trim();
};

const tableComparable=(raw: TableSortValue): {kind: 'empty' | 'number' | 'text';value: number | string}=> {
  if(raw===null || raw===undefined || raw==='') return {kind:'empty',value:''};
  if(raw instanceof Date) return Number.isNaN(raw.getTime())
    ? {kind:'empty',value:''} : {kind:'number',value:raw.getTime()};
  if(typeof raw==='number') return Number.isFinite(raw)
    ? {kind:'number',value:raw} : {kind:'empty',value:''};
  if(typeof raw==='boolean') return {kind:'number',value:raw?1:0};
  const text=String(raw).trim();
  if(!text || text==='—' || text==='-' || text.toUpperCase()==='N/A') return {kind:'empty',value:''};
  // ISO and Australian display dates must sort chronologically, not alphabetically.
  const iso=text.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
  if(iso) {
    const ms=Date.parse(text.length>10 ? text.replace(' ','T') : text+'T00:00:00Z');
    if(!Number.isNaN(ms)) return {kind:'number',value:ms};
  }
  const au=text.match(/^(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\s+(\d{4})(?:,?\s+.*)?$/i);
  if(au) {
    const months=['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
    const month=months.indexOf(au[2].toLowerCase().slice(0,3));
    const ms=Date.UTC(Number(au[3]),month,Number(au[1]));
    if(month>=0 && !Number.isNaN(ms)) return {kind:'number',value:ms};
  }
  if(/^[-+]?[$£€]?\s*[\d,]+(?:\.\d+)?%?$/.test(text)) {
    const n=Number(text.replace(/[$£€,%\s]/g,''));
    if(Number.isFinite(n)) return {kind:'number',value:n};
  }
  return {kind:'text',value:text};
};

export const compareTableValues=(left: TableSortValue,right: TableSortValue,direction: 'asc' | 'desc'='asc'): number=> {
  const a=tableComparable(left),b=tableComparable(right);
  // Unavailable data always remains at the bottom, regardless of sort direction.
  if(a.kind==='empty') return b.kind==='empty'?0:1;
  if(b.kind==='empty') return -1;
  const compared=a.kind==='number' && b.kind==='number'
    ? (a.value as number)-(b.value as number)
    : TABLE_COLLATOR.compare(String(a.value),String(b.value));
  return direction==='desc'?-compared:compared;
};

// For paginated/lazy-loaded tables: sort the entire filtered dataset first, then
// paginate. Accessors refer only to existing read-only fields.
export const sortTableRecords=<T,>(
  records: readonly T[],sort: TableSort | null,columns: ReadonlyArray<(record: T)=>TableSortValue>
): T[]=> {
  if(!sort || !columns[sort.column]) return [...records];
  const pick=columns[sort.column];
  return records.map((record,index)=>({record,index,value:pick(record)})).sort((a,b)=>
    compareTableValues(a.value,b.value,sort.direction) || a.index-b.index
  ).map(entry=>entry.record);
};

export const Table=({
  children,className,sortable=true,sort: controlledSort,onSortChange,manualSort=false
}: {
  children: React.ReactNode;className?: string;sortable?: boolean;
  sort?: TableSort | null;onSortChange?: (next: TableSort)=> void;manualSort?: boolean;
})=> {
  const [localSort,setLocalSort]=React.useState<TableSort | null>(null);
  const headings=tableHeadings(children);
  const requested=controlledSort===undefined?localSort:controlledSort;
  const sort=sortable && requested && headings[requested.column]===requested.label ? requested : null;
  const changeSort=(column: number,label: string)=> {
    const current=sort?.column===column && sort.label===label ? sort:null;
    const next: TableSort={column,label,direction:current?.direction==='asc'?'desc':'asc'};
    if(onSortChange) onSortChange(next);
    else setLocalSort(next);
  };
  return (
    <TableSorting.Provider value={{enabled:sortable,sort,changeSort,manualSort:manualSort || !sortable}}>
      <div className="overflow-x-auto w-full max-w-full premium-scrollbar">
        <table className={cn('w-full text-left text-sm text-gray-600 whitespace-nowrap',className)}>{children}</table>
      </div>
    </TableSorting.Provider>
  );
};

export const Thead=({children}: any)=> (
  <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">{children}</thead>
);

export const Tbody=({children}: any)=> {
  const sorting=React.useContext(TableSorting);
  if(!sorting?.sort || sorting.manualSort) return <tbody className="divide-y divide-gray-100">{children}</tbody>;
  const rows=tableChildrenOfType(children,Tr);
  // Mixed non-row children should retain their exact original React order.
  const original=React.Children.toArray(children);
  if(rows.length<2 || (original.length>rows.length && original.some(child=>
    !React.isValidElement(child) || (child.type!==Tr && child.type!==React.Fragment)
  ))) return <tbody className="divide-y divide-gray-100">{children}</tbody>;
  const sort=sorting.sort;
  const ordered=rows.map((row,index)=>({row,index,value:tableColumnValue(row,sort.column)})).sort((a,b)=>
    compareTableValues(a.value,b.value,sort.direction) || a.index-b.index
  ).map(entry=>entry.row);
  return <tbody className="divide-y divide-gray-100">{ordered}</tbody>;
};

export const Tr=({children,className,...props}: any)=> (
  <tr className={cn('bg-white hover:bg-gray-50 transition-colors',className)} {...props}>{children}</tr>
);

export const Th=({children,className,sortable=true,...props}: any)=> {
  const sorting=React.useContext(TableSorting);
  const label=tableReactText(children).trim();
  const canSort=Boolean(sorting?.enabled && sortable && label && !/^actions?$/i.test(label) && !props.colSpan && !props.rowSpan);
  const active=Boolean(canSort && sorting?.sort?.label===label);
  const isRightAligned=String(className || '').split(/\s+/).includes('text-right');
  const onHeaderClick=(event: React.MouseEvent<HTMLButtonElement>)=> {
    const cell=event.currentTarget.closest('th') as HTMLTableCellElement | null;
    if(cell && sorting) sorting.changeSort(cell.cellIndex,label);
  };
  return (
    <th scope="col" className={cn(canSort ? 'px-4 py-1.5 font-semibold' : 'px-4 py-3 font-semibold',className)}
      aria-sort={active?(sorting?.sort?.direction==='asc'?'ascending':'descending'):undefined} {...props}>
      {canSort
        ? <button type="button" onClick={onHeaderClick}
            className={cn('group inline-flex min-h-10 w-full items-center gap-1.5 rounded text-inherit outline-none hover:text-teal-700 focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2',isRightAligned ? 'justify-end text-right' : 'text-left')}
            style={{font:'inherit'}}
            aria-label={'Sort by '+label+(active?(sorting?.sort?.direction==='asc'?', currently ascending' : ', currently descending'):'')}
            title={'Sort by '+label}>
            <span>{children}</span>
            {active
              ? sorting?.sort?.direction==='asc' ? <FiIcons.FiChevronUp aria-hidden="true" className="shrink-0 text-teal-700" /> : <FiIcons.FiChevronDown aria-hidden="true" className="shrink-0 text-teal-700" />
              : <span aria-hidden="true" className="flex shrink-0 flex-col -space-y-1 text-gray-400 opacity-60 group-hover:opacity-100"><FiIcons.FiChevronUp className="h-3 w-3" /><FiIcons.FiChevronDown className="h-3 w-3" /></span>}
          </button>
        : children}
    </th>
  );
};

export const Td=({children,className,sortValue: _sortValue,...props}: any)=> (
  <td className={cn('px-4 py-3 transition-colors',className)} {...props}>{children}</td>
);

export const ResponsiveContainer=({children,className=''}: any)=> (
  <div className={cn('w-full overflow-x-auto',className)}>{children}</div>
);

interface ModalProps {
  isOpen: boolean;
  onClose: ()=> void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
}

export const Modal=({isOpen,onClose,title,children,footer,size='lg'}: ModalProps)=> {
  if (!isOpen) return null;
  const widthMap={md: 'max-w-lg',lg: 'max-w-3xl',xl: 'max-w-6xl'};
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-gray-900/50 p-0 backdrop-blur-sm animate-in fade-in duration-200 sm:items-center sm:p-4">
      <div className={`bg-white rounded-t-2xl shadow-xl w-full min-w-0 ${widthMap[size]} overflow-hidden flex flex-col max-h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] sm:max-h-[90vh] sm:rounded-xl animate-in zoom-in-95 duration-200`}>
        <div className="flex justify-between items-center p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight">{title}</h3>
          <button onClick={onClose} className="min-h-11 min-w-11 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all">
            <SafeIcon icon={FiIcons.FiX} className="w-5 h-5" />
          </button>
        </div>
        <div className="p-3 sm:p-4 overflow-y-auto overflow-x-hidden overscroll-contain flex-1 min-w-0 premium-scrollbar">{children}</div>
        {footer && <div className="invoiceflow-modal-footer p-3 sm:p-4 border-t border-gray-100 bg-gray-50 flex flex-wrap justify-end items-center gap-2 sm:gap-3">{footer}</div>}
      </div>
    </div>
  );
};